'use client'

import { type FC, useCallback, useRef, useState } from 'react'

import {
  ResponsiveImage,
  RotateClockwise,
  TypeStyleHeadlineExtraSmall,
  useResizeObserver,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../_providers/TranslationProvider.client'

import {
  FIT_GUIDE_IMAGE_ASPECT_RATIO,
  FIT_GUIDE_IMAGE_WIDTH,
  type SizeChartTable,
} from './sizeChartData'
import * as Styles from './SizeChartFitGuideSection.styles'

const MISSING_VALUE = '—'
const SCROLL_EDGE_THRESHOLD = 8
const CARAT_SIZE = '16px'

export type SizeChartFitGuideSectionProps = {
  table: SizeChartTable
}

export const SizeChartFitGuideSection: FC<SizeChartFitGuideSectionProps> = ({ table }) => {
  const {
    sizesSection: { sizeChart: strings },
  } = useTranslation()

  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [scrollsLeft, setScrollsLeft] = useState(false)
  const [scrollsRight, setScrollsRight] = useState(false)
  // With few enough products to fit, the row never scrolls and the arrows would be dead weight.
  const [overflows, setOverflows] = useState(false)

  const checkScrollable = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth
    setOverflows(hasOverflow)
    if (hasOverflow) {
      setScrollsLeft(el.scrollLeft > SCROLL_EDGE_THRESHOLD)
      setScrollsRight(el.scrollLeft + el.clientWidth + SCROLL_EDGE_THRESHOLD < el.scrollWidth)
    } else {
      setScrollsLeft(false)
      setScrollsRight(false)
    }
  }, [])

  useResizeObserver(scrollerRef, checkScrollable)

  const scrollToEdge = useCallback((edge: 'start' | 'end') => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollLeft = edge === 'start' ? 0 : el.scrollWidth - el.clientWidth
  }, [])

  const tiles = table.visualFiltersSection?.tiles ?? []
  if (tiles.length === 0) return null

  const bulletCount = tiles.reduce((count, tile) => Math.max(count, tile.iconBadges.length), 0)

  return (
    <>
      <Styles.SectionHeader>
        <TypeStyleHeadlineExtraSmall asTag='h2'>{table.title}</TypeStyleHeadlineExtraSmall>

        {overflows ? (
          <Styles.ScrollControls>
            <Styles.HorizontalScrollButton
              aria-disabled={!scrollsLeft}
              aria-label={strings.aria.scrollToStart}
              disabled={!scrollsLeft}
              onClick={() => scrollToEdge('start')}
            >
              <Styles.HorizontalScrollButtonIcon
                height={CARAT_SIZE}
                rotateClockwise={RotateClockwise.Half}
                width={CARAT_SIZE}
              />
            </Styles.HorizontalScrollButton>

            <Styles.HorizontalScrollButton
              aria-disabled={!scrollsRight}
              aria-label={strings.aria.scrollToEnd}
              disabled={!scrollsRight}
              onClick={() => scrollToEdge('end')}
            >
              <Styles.HorizontalScrollButtonIcon height={CARAT_SIZE} width={CARAT_SIZE} />
            </Styles.HorizontalScrollButton>
          </Styles.ScrollControls>
        ) : null}
      </Styles.SectionHeader>

      {table.subtitle ? <Styles.SectionBody asTag='p'>{table.subtitle}</Styles.SectionBody> : null}

      <Styles.FitGuideTableWrap
        aria-label={table.title ?? undefined}
        onScroll={checkScrollable}
        ref={scrollerRef}
        role='group'
        tabIndex={0}
      >
        <Styles.Table>
          <thead>
            <tr>
              {tiles.map(tile => (
                <Styles.ProductImageCell key={tile.id}>
                  <Styles.ProductImageFrame>
                    <ResponsiveImage
                      // The product name in the header below already names the product.
                      alt=''
                      aria-hidden='true'
                      aspectRatios={{
                        sm: FIT_GUIDE_IMAGE_ASPECT_RATIO,
                        md: FIT_GUIDE_IMAGE_ASPECT_RATIO,
                      }}
                      loading='lazy'
                      src={tile.image}
                      widths={{
                        unit: 'px',
                        sm: FIT_GUIDE_IMAGE_WIDTH,
                        md: FIT_GUIDE_IMAGE_WIDTH,
                      }}
                    />
                  </Styles.ProductImageFrame>
                </Styles.ProductImageCell>
              ))}
              <Styles.SpacerCell aria-hidden='true' />
            </tr>
            <tr>
              {tiles.map(tile => (
                <Styles.ProductHeaderCell key={tile.id} scope='col'>
                  {tile.ctaText}
                </Styles.ProductHeaderCell>
              ))}
              <Styles.SpacerCell aria-hidden='true' />
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: bulletCount }, (_, bulletIndex) => (
              <Styles.FitGuideRow key={bulletIndex}>
                {tiles.map(tile => (
                  <Styles.FitGuideCell key={tile.id}>
                    {tile.iconBadges[bulletIndex]?.iconText || MISSING_VALUE}
                  </Styles.FitGuideCell>
                ))}
                <Styles.SpacerCell aria-hidden='true' />
              </Styles.FitGuideRow>
            ))}
          </tbody>
        </Styles.Table>
      </Styles.FitGuideTableWrap>
    </>
  )
}
